import { useEffect, useRef, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

/* eslint-disable i18next/no-literal-string -- keys the states are compared by, never shown to anyone */
export const SETTLED_STATE = {
  loading: 'loading',
  empty: 'empty',
  rows: 'rows',
  error: 'error',
} as const;
/* eslint-enable i18next/no-literal-string */

export type SettledState = (typeof SETTLED_STATE)[keyof typeof SETTLED_STATE];

interface SettledContentProps {
  /**
   * Which of the mutually exclusive states the content is in. Changing it
   * crossfades; the value itself is never rendered.
   */
  state: SettledState;
  /**
   * The box every state shares — its size, padding and scrolling. It is the
   * grid the states stack in, so it must not carry a display of its own.
   */
  className?: string;
  /** How each state fills that box. Applied per state, not to the box. */
  contentClassName?: string;
  children: ReactNode;
}

/**
 * `--duration-slower`, written as a number because these animations run in JS,
 * which never reads the CSS custom properties — the value is the token's, the
 * literal is unavoidable.
 *
 * The top of the scale on purpose: anything quicker read as an animation cut
 * short rather than a handover. It can afford to be the slowest step because
 * only the empty answer takes this path — content arriving is instant, and a
 * wait under the indicator threshold shows nothing at all.
 */
export const SETTLED_EXIT_SECONDS = 0.5;

/** The empty state arriving, once the placeholder has finished leaving. */
const crossfade = { duration: SETTLED_EXIT_SECONDS, ease: 'easeOut' } as const;

/** Content replacing its own placeholder, and a failure, both arrive at once. */
const immediate = { duration: 0 } as const;

/** The placeholder leaving, which the next state waits for. */
const placeholderExit = { duration: SETTLED_EXIT_SECONDS, ease: 'easeOut' } as const;

/** Where a leaving block came from and where it is going. */
interface SettledHandover {
  from: SettledState | undefined;
  to: SettledState;
}

/**
 * Only a placeholder is worth dissolving, and only into an empty answer. The
 * leaving element cannot know either end on its own — `AnimatePresence` passes
 * `custom` down to it for exactly this.
 *
 * Both halves of the test matter. Handing over to the records is not worth a
 * fade: holding them behind one leaves a blank frame where rows should already
 * be. And content handing over to an empty answer is not a placeholder leaving
 * — it is one answer replacing another, which is why switching between two
 * loaded folders used to sit through half a second of nothing.
 */
const variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: ({ from, to }: SettledHandover) => ({
    opacity: 0,
    transition: isPlaceholderHandover(from, to) ? placeholderExit : immediate,
  }),
};

/**
 * The one handover worth waiting through: a placeholder that was actually drawn,
 * giving way to an answer of nothing. The user stops scanning and starts
 * reading, and the dissolve marks that.
 *
 * Everything else is content replacing content and appears at once — including
 * two loaded answers trading places, which is what a folder switch is, and a
 * failure, since nobody should sit through an easing curve to learn something
 * went wrong.
 */
const isPlaceholderHandover = (from: SettledState | undefined, to: SettledState) =>
  from === SETTLED_STATE.loading && to === SETTLED_STATE.empty;

/**
 * Animates a block's state changes, and only its state changes.
 *
 * First paint is not a change — nothing preceded it — so it carries no
 * entrance; the section it sits in already crossfades as a whole when the route
 * arrives. What follows does animate, but only where the meaning moves: content
 * replacing its own placeholder is one moment and appears at once, while the
 * answer turning out to be empty, or the request failing, crossfades because
 * the user is about to read rather than scan.
 */
export function SettledContent(props: SettledContentProps) {
  const shouldReduceMotion = useReducedMotion();
  const previousState = useRef<SettledState | undefined>(undefined);

  // A state whose children are nothing has nothing on screen: an answer that
  // arrives before the placeholder is worth showing renders null, and that is
  // not a state to hand over from. It neither holds the stage on its way out
  // nor counts as the thing the next state replaces, so the answer lands as a
  // first paint instead of costing a fade out of blankness and a fade back in.
  const isDrawn =
    props.children !== null && props.children !== undefined && props.children !== false;

  const from = previousState.current;
  // Advanced after commit rather than during render: a render can be discarded and
  // re-run, and writing in the body makes the second pass read what the first just
  // wrote, which changes the transition it computes.
  useEffect(() => {
    if (isDrawn) previousState.current = props.state;
  }, [props.state, isDrawn]);

  // Nothing stacks without the crossfade, so the box and its one state collapse
  // into a single element rather than nesting an empty grid.
  if (shouldReduceMotion) {
    return (
      <div className={`${props.className ?? ''} ${props.contentClassName ?? ''}`}>
        {props.children}
      </div>
    );
  }

  // The placeholder finishes leaving before its replacement arrives. Waiting
  // costs a beat, but only on a load that already showed a placeholder — and
  // one is only shown once the wait passed the threshold, so the beat lands
  // where nobody is counting frames any more.
  //
  // The grid keeps both children in one cell for the frame they share, so the
  // outgoing block never leaves the flow. `popLayout` took it out with absolute
  // positioning and dropped it in the wrong place.
  return (
    <div className={`grid ${props.className ?? ''}`}>
      <AnimatePresence mode="wait" initial={false} custom={{ from, to: props.state }}>
        {isDrawn && (
          <motion.div
            key={props.state}
            data-testid="settled-state"
            className={`col-start-1 row-start-1 ${props.contentClassName ?? ''}`}
            custom={{ from, to: props.state }}
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            // The placeholder appears at once: its arrival is the least meaningful
            // moment on the page, and fading it in makes the rows inside look like
            // they land separately, each already part-way through its own pulse.
            transition={
              props.state === SETTLED_STATE.loading
                ? immediate
                : isPlaceholderHandover(from, props.state)
                  ? crossfade
                  : immediate
            }
          >
            {props.children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
